import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, TriangleAlert, X } from "lucide-react";
import { useAutomationsApi } from "../api/automationsApi";
import { useTags } from "../hooks/useTags";
import { useUsers } from "../hooks/useUsers";
import { useFunnels } from "../hooks/useFunnels";

const EMPTY = {
  name: "",
  description: "",
  enabled: false,
  trigger: "MESSAGE_RECEIVED",
  triggerConfig: { days: 3 },
  conditions: { stages: [], statuses: [], tagIds: [], userIds: [], withoutOwner: false },
  actions: [],
  dailyLimit: 50,
  cooldownMinutes: 1440,
};

const AutomationModal = ({ isOpen, onClose, onSaved, automation, catalog }) => {
  const api = useAutomationsApi();
  const { tags } = useTags();
  const { users } = useUsers();
  const { funnels } = useFunnels();

  const [form, setForm] = useState(EMPTY);
  const [simulation, setSimulation] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(automation ? { ...EMPTY, ...automation } : EMPTY);
    setSimulation(null);
  }, [automation, isOpen]);

  if (!isOpen || !catalog) return null;

  const steps = [...new Set(funnels.flatMap((funnel) => funnel.steps || []))];
  const external = form.actions.some((action) => catalog.actions[action.type]?.external);

  const patch = (changes) => setForm((prev) => ({ ...prev, ...changes }));

  const addAction = (type) =>
    patch({ actions: [...form.actions, { type }] });

  const updateAction = (index, changes) =>
    patch({
      actions: form.actions.map((action, position) =>
        position === index ? { ...action, ...changes } : action,
      ),
    });

  const removeAction = (index) =>
    patch({ actions: form.actions.filter((_, position) => position !== index) });

  const handleSimulate = async () => {
    try {
      setSimulation(await api.simulate(form));
    } catch (error) {
      toast.error(error.message || "Não foi possível simular.");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, confirmExternal: true };
      if (automation) await api.update(automation.id, payload);
      else await api.create(payload);

      toast.success("Automação salva.");
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const field =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex h-[88vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {automation ? "Editar automação" : "Nova automação"}
          </h2>
          <button onClick={onClose} className="rounded p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Nome</span>
              <input
                className={field}
                value={form.name}
                onChange={(event) => patch({ name: event.target.value })}
                placeholder="Ex.: Cobrar retorno após 3 dias"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Quando acontecer</span>
              <select
                className={field}
                value={form.trigger}
                onChange={(event) => patch({ trigger: event.target.value })}
              >
                {Object.entries(catalog.triggers).map(([value, item]) => (
                  <option key={value} value={value}>{item.label}</option>
                ))}
              </select>
            </label>
          </div>

          {form.trigger === "IDLE_DAYS" && (
            <label className="block max-w-xs">
              <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Dias parada</span>
              <input
                type="number"
                min="1"
                className={field}
                value={form.triggerConfig?.days ?? 3}
                onChange={(event) =>
                  patch({ triggerConfig: { days: Number(event.target.value) } })
                }
              />
            </label>
          )}

          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Só quando a conversa estiver
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Etapa (vazio = qualquer)</span>
                <select
                  multiple
                  className={`${field} h-24`}
                  value={form.conditions.stages || []}
                  onChange={(event) =>
                    patch({
                      conditions: {
                        ...form.conditions,
                        stages: [...event.target.selectedOptions].map((option) => option.value),
                      },
                    })
                  }
                >
                  {steps.map((step) => <option key={step} value={step}>{step}</option>)}
                </select>
              </label>

              <label className="block">
                <span className="mb-1 block text-xs text-gray-600 dark:text-gray-400">Status (vazio = qualquer)</span>
                <select
                  multiple
                  className={`${field} h-24`}
                  value={form.conditions.statuses || []}
                  onChange={(event) =>
                    patch({
                      conditions: {
                        ...form.conditions,
                        statuses: [...event.target.selectedOptions].map((option) => option.value),
                      },
                    })
                  }
                >
                  {catalog.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
            </div>

            <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-500"
                checked={form.conditions.withoutOwner || false}
                onChange={(event) =>
                  patch({
                    conditions: { ...form.conditions, withoutOwner: event.target.checked },
                  })
                }
              />
              Apenas conversas sem responsável
            </label>
          </div>

          <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Então faça</p>
              <select
                value=""
                onChange={(event) => event.target.value && addAction(event.target.value)}
                className="rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">Adicionar ação...</option>
                {Object.entries(catalog.actions).map(([value, item]) => (
                  <option key={value} value={value}>
                    {item.label}{item.external ? " (externa)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {form.actions.length === 0 && (
              <p className="py-3 text-center text-sm text-gray-500">Nenhuma ação ainda.</p>
            )}

            <div className="space-y-2">
              {form.actions.map((action, index) => (
                <div key={index} className="rounded-lg bg-gray-50 p-2 dark:bg-gray-800">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      {catalog.actions[action.type]?.label || action.type}
                    </span>
                    <button
                      onClick={() => removeAction(index)}
                      className="text-xs text-red-600 hover:underline dark:text-red-400"
                    >
                      remover
                    </button>
                  </div>

                  {action.type === "move_stage" && (
                    <select className={field} value={action.stage || ""} onChange={(e) => updateAction(index, { stage: e.target.value })}>
                      <option value="">Escolha a etapa</option>
                      {steps.map((step) => <option key={step} value={step}>{step}</option>)}
                    </select>
                  )}

                  {action.type === "set_status" && (
                    <select className={field} value={action.status || ""} onChange={(e) => updateAction(index, { status: e.target.value })}>
                      <option value="">Escolha o status</option>
                      {catalog.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                  )}

                  {["add_tag", "remove_tag"].includes(action.type) && (
                    <select className={field} value={action.tagId || ""} onChange={(e) => updateAction(index, { tagId: Number(e.target.value) })}>
                      <option value="">Escolha a etiqueta</option>
                      {tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}
                    </select>
                  )}

                  {action.type === "assign_user" && (
                    <select className={field} value={action.userId || ""} onChange={(e) => updateAction(index, { userId: Number(e.target.value) })}>
                      <option value="">Escolha o responsável</option>
                      {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
                    </select>
                  )}

                  {["notify_team", "send_message"].includes(action.type) && (
                    <textarea
                      className={field}
                      rows={2}
                      placeholder={action.type === "send_message" ? "Mensagem enviada ao cliente" : "Aviso para a equipe"}
                      value={action.type === "send_message" ? action.text || "" : action.message || ""}
                      onChange={(e) =>
                        updateAction(index, action.type === "send_message"
                          ? { text: e.target.value }
                          : { message: e.target.value })
                      }
                    />
                  )}

                  {action.type === "webhook" && (
                    <div className="space-y-2">
                      <input className={field} placeholder="https://..." value={action.url || ""} onChange={(e) => updateAction(index, { url: e.target.value })} />
                      <input className={field} placeholder="Segredo para assinatura (opcional)" value={action.secret || ""} onChange={(e) => updateAction(index, { secret: e.target.value })} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Teto por dia</span>
              <input type="number" min="1" className={field} value={form.dailyLimit} onChange={(e) => patch({ dailyLimit: Number(e.target.value) })} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Rearme por conversa (min)</span>
              <input type="number" min="0" className={field} value={form.cooldownMinutes} onChange={(e) => patch({ cooldownMinutes: Number(e.target.value) })} />
            </label>
          </div>

          {external && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Esta automação age fora do CRM (mensagem ao cliente ou chamada
                externa). Ela só roda dentro do horário de atendimento e respeita
                o teto diário. <strong>Simule antes de ativar.</strong>
              </p>
            </div>
          )}

          {simulation && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-900 dark:bg-blue-900/20">
              <p className="font-medium text-blue-900 dark:text-blue-200">
                {simulation.matched} conversa(s) atendem às condições agora
                {simulation.matched > form.dailyLimit && " — acima do teto diário"}
              </p>
              {simulation.sample.length > 0 && (
                <p className="mt-1 text-xs text-blue-800 dark:text-blue-300">
                  Ex.: {simulation.sample.map((item) => item.name).join(", ")}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-gray-200 p-4 dark:border-gray-700">
          <button
            onClick={handleSimulate}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Simular
          </button>

          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 dark:border-gray-600 dark:text-gray-300">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1 rounded-lg bg-green-500 px-4 py-2 text-sm text-white hover:bg-green-600 disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              {saving ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationModal;
